/**
 * Distances and areas for the survey site map (feet / square feet). One implementation for every
 * app, because these numbers feed the permit: the main app and the Sales app must agree to the
 * foot. Moved from client/src/lib/geo/haversine.ts, which now re-exports these (its test is the
 * parity proof).
 */
const EARTH_RADIUS_METERS = 6371000;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Calculate distance between two lat/lng points in meters using the Haversine formula */
export function haversineDistanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const sinHalfLat = Math.sin(dLat / 2);
  const sinHalfLng = Math.sin(dLng / 2);
  const h = sinHalfLat * sinHalfLat + Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * sinHalfLng * sinHalfLng;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
}

/** Calculate total distance of a polyline path in feet */
export function calculatePathDistanceFt(points: { lat: number; lng: number }[]): number {
  if (points.length < 2) return 0;
  let totalMeters = 0;
  for (let i = 1; i < points.length; i++) {
    totalMeters += haversineDistanceMeters(points[i - 1], points[i]);
  }
  return Math.round(totalMeters * 3.28084);
}

/** Calculate area of a polygon in square feet using the Shoelace formula on projected coordinates */
export function calculatePolygonAreaSqFt(points: { lat: number; lng: number }[]): number {
  if (points.length < 3) return 0;
  const refLat = points[0].lat;
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLng = 111320 * Math.cos(toRadians(refLat));

  // Project to meters from reference point
  const projected = points.map((p) => ({
    x: (p.lng - points[0].lng) * metersPerDegreeLng,
    y: (p.lat - points[0].lat) * metersPerDegreeLat,
  }));

  // Shoelace formula
  let area = 0;
  for (let i = 0; i < projected.length; i++) {
    const j = (i + 1) % projected.length;
    area += projected[i].x * projected[j].y;
    area -= projected[j].x * projected[i].y;
  }
  const sqMeters = Math.abs(area) / 2;
  return Math.round(sqMeters * 10.7639); // sq meters to sq feet
}
